import type { TranslationBundle } from '@bms/common';

/**
 * The `shell` namespace: app chrome outside any one screen — the router's
 * no-site and error-boundary fallbacks, the install banner, the card reader
 * badge, the sidebar frame, the login screen's own furniture, the main
 * layout's toasts and theme controls, and the card-tap notification toasts.
 * Keys are read as `shell.<key>` through `useTranslation()`.
 */
export const shell: TranslationBundle = {
  en: {
    noSites: {
      title: 'No sites configured',
      body: 'Please contact your administrator to set up a site.',
    },
    errorBoundary: {
      title: 'Something went wrong',
      body: 'This page ran into an unexpected error. You can try again.',
      retry: 'Try again',
    },
    install: {
      title: 'Install BMS Control',
      description: 'Add it to your home screen for quick access.',
      action: 'Install',
      dismiss: 'Dismiss install prompt',
    },
    nfcAgent: {
      connected: 'Card Reader: Connected',
      noSam: 'Card Reader: No SAM',
      disconnected: 'Card Reader: Disconnected',
      ready: 'Reader ready',
      noSamShort: 'Reader: no SAM',
      keymaster: 'Reader in use by KeyMaster',
      notThisSite: 'Reader: SAM not set up for this site',
      none: 'No reader',
    },
    login: {
      product: 'Corteh GRMS',
      buildAria: 'Edge build',
    },
    edgeVersion: 'Edge version',
    themeToggle: {
      light: 'Theme: light. Switch to dark.',
      dark: 'Theme: dark. Switch to following the system.',
      system: 'Theme: following the system. Switch to light.',
    },
    themeOption: {
      light: 'Light',
      dark: 'Dark',
      system: 'System',
    },
    livePaused: 'Live updates paused since {{time}} · retrying · showing last known values',
    offline: "You're offline — showing cached app, live data unavailable",
    backOnline: 'Back online',
    skipToContent: 'Skip to content',
    brandAlt: 'Corteh',
    housekeepingFallbackName: 'Housekeeping',
    sites: 'Sites',
    sidebar: {
      title: 'Sidebar',
      description: 'Displays the mobile sidebar.',
      toggle: 'Toggle Sidebar',
    },
    datePicker: {
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      time: 'Time',
      clear: 'Clear',
      done: 'Done',
      anyDate: 'Any date',
      pickDateTime: 'Pick a date and time',
      clearAria: 'Clear {{label}}',
    },
    cardTap: {
      dismissAria: 'Dismiss card tap notification',
      writeNotConfirmedTitle: 'Write not confirmed',
      writeNotConfirmedDescription:
        'The encoder could not confirm the write to {{uid}}; the card stays pending. Do not hand it out: tap it again to finish writing.',
      cardEncodedTitle: 'Card encoded',
      cardEncodedDescription: 'Card {{uid}} is written and confirmed.',
      writeFailedTitle: 'Card written; Edge save failed',
      writeFailedDescription: '{{error}} Keep this card here and retry Save; do not encode it again or hand it out yet.',
      writeFailedDefaultError: 'Edge did not confirm the write.',
      retrySave: 'Retry save',
      encodingTitle: 'Encoding card',
      encodingDescription: 'Writing pending card {{uid}}.',
      encodeFailedTitle: 'Card encoding failed',
      encodeFailedReaderError: 'The reader could not write this card.',
      confirmMissingTitle: 'Card confirmation missing',
      confirmMissingDescription: 'The encoder did not return a completed write report. The pending card was not confirmed.',
      encodeFailedPayloadError: 'Could not prepare the card payload.',
      cardRecognizedTitle: 'Card recognized',
      cardSentence: '{{type}} {{uid}} is {{status}}.',
      showInSearch: 'Show in search',
      unsupportedCardTitle: 'Unsupported card',
      unsupportedCardDescription: 'This carrier is not supported for Corteh card encoding.',
      pendingCardTitle: 'Pending card detected',
      pendingCardDescription: 'This card exists in BMS and needs to be encoded.',
      encodeCard: 'Encode card',
      cardLookupFailedTitle: 'Could not read card status',
      cardLookupFailedDefault: 'The card lookup failed.',
      unknownCardTitle: 'Unknown card detected',
      unknownCardNeedsManager: 'Ask a manager to issue this card before it can be used.',
      unknownCardWalkInPrompt: 'Blank card. New guest, or an extra key for a room?',
      unknownCardIssuePrompt: 'A blank card. Issue it to a room, lock, lock group or role.',
      walkIn: 'New guest',
      extraKey: 'Extra key',
      issueThisCard: 'Issue this card',
      cardIssuedTitle: 'Card issued',
      cardIssuedDescription: 'Card {{uid}} is ready to use.',
    },
  },
  hr: {
    noSites: {
      title: 'Nema konfiguriranih lokacija',
      body: 'Obratite se administratoru radi postavljanja lokacije.',
    },
    errorBoundary: {
      title: 'Nešto je pošlo po zlu',
      body: 'Došlo je do neočekivane pogreške na ovoj stranici. Možete pokušati ponovno.',
      retry: 'Pokušaj ponovno',
    },
    install: {
      title: 'Instaliraj BMS Control',
      description: 'Dodajte ga na početni zaslon za brz pristup.',
      action: 'Instaliraj',
      dismiss: 'Odbaci ponudu za instalaciju',
    },
    nfcAgent: {
      connected: 'Čitač kartica: Povezano',
      noSam: 'Čitač kartica: Nema SAM',
      disconnected: 'Čitač kartica: Odspojeno',
      ready: 'Čitač spreman',
      noSamShort: 'Čitač: nema SAM-a',
      keymaster: 'KeyMaster koristi čitač',
      notThisSite: 'Čitač: SAM nije postavljen za ovu lokaciju',
      none: 'Nema čitača',
    },
    login: {
      product: 'Corteh GRMS',
      buildAria: 'Verzija Edgea',
    },
    edgeVersion: 'Verzija Edgea',
    themeToggle: {
      light: 'Tema: svijetla. Prijeđi na tamnu.',
      dark: 'Tema: tamna. Prijeđi na praćenje sustava.',
      system: 'Tema: prati sustav. Prijeđi na svijetlu.',
    },
    themeOption: {
      light: 'Svijetla',
      dark: 'Tamna',
      system: 'Sustav',
    },
    livePaused: 'Ažuriranja uživo pauzirana od {{time}} · ponovno pokušavamo · prikazane su zadnje poznate vrijednosti',
    offline: 'Niste povezani — prikazuje se spremljena aplikacija, podaci uživo nisu dostupni',
    backOnline: 'Ponovno na mreži',
    skipToContent: 'Preskoči na sadržaj',
    brandAlt: 'Corteh',
    housekeepingFallbackName: 'Domaćinstvo',
    sites: 'Lokacije',
    sidebar: {
      title: 'Bočna traka',
      description: 'Prikazuje bočnu traku na mobilnom uređaju.',
      toggle: 'Otvori/zatvori bočnu traku',
    },
    datePicker: {
      previousMonth: 'Prethodni mjesec',
      nextMonth: 'Sljedeći mjesec',
      time: 'Vrijeme',
      clear: 'Očisti',
      done: 'Gotovo',
      anyDate: 'Bilo koji datum',
      pickDateTime: 'Odaberite datum i vrijeme',
      clearAria: 'Očisti {{label}}',
    },
    cardTap: {
      dismissAria: 'Odbaci obavijest o očitanju kartice',
      writeNotConfirmedTitle: 'Zapisivanje nije potvrđeno',
      writeNotConfirmedDescription:
        'Koder nije mogao potvrditi zapis na {{uid}}; kartica ostaje na čekanju. Nemojte je predati: ponovno je približite za dovršetak zapisivanja.',
      cardEncodedTitle: 'Kartica kodirana',
      cardEncodedDescription: 'Kartica {{uid}} je zapisana i potvrđena.',
      writeFailedTitle: 'Kartica zapisana; spremanje na Edgeu nije uspjelo',
      writeFailedDescription: '{{error}} Zadržite ovu karticu ovdje i ponovite spremanje; nemojte je ponovno kodirati ni predati.',
      writeFailedDefaultError: 'Edge nije potvrdio zapisivanje.',
      retrySave: 'Pokušaj spremiti ponovno',
      encodingTitle: 'Kodiranje kartice',
      encodingDescription: 'Zapisivanje kartice na čekanju {{uid}}.',
      encodeFailedTitle: 'Kodiranje kartice nije uspjelo',
      encodeFailedReaderError: 'Čitač nije mogao zapisati ovu karticu.',
      confirmMissingTitle: 'Potvrda kartice nedostaje',
      confirmMissingDescription: 'Koder nije vratio dovršeno izvješće o zapisu. Kartica na čekanju nije potvrđena.',
      encodeFailedPayloadError: 'Nije bilo moguće pripremiti podatke kartice.',
      cardRecognizedTitle: 'Kartica prepoznata',
      cardSentence: '{{type}} {{uid}} je {{status}}.',
      showInSearch: 'Prikaži u pretrazi',
      unsupportedCardTitle: 'Nepodržana kartica',
      unsupportedCardDescription: 'Ovaj nosač nije podržan za Corteh kodiranje kartica.',
      pendingCardTitle: 'Otkrivena kartica na čekanju',
      pendingCardDescription: 'Ova kartica postoji u BMS-u i treba je kodirati.',
      encodeCard: 'Kodiraj karticu',
      cardLookupFailedTitle: 'Status kartice nije moguće očitati',
      cardLookupFailedDefault: 'Provjera kartice nije uspjela.',
      unknownCardTitle: 'Otkrivena nepoznata kartica',
      unknownCardNeedsManager: 'Zatražite od voditelja da izda ovu karticu prije upotrebe.',
      unknownCardWalkInPrompt: 'Prazna kartica. Novi gost ili dodatni ključ za sobu?',
      unknownCardIssuePrompt: 'Prazna kartica. Izdajte je za sobu, bravu, grupu brava ili ulogu.',
      walkIn: 'Novi gost',
      extraKey: 'Dodatni ključ',
      issueThisCard: 'Izdaj ovu karticu',
      cardIssuedTitle: 'Kartica izdana',
      cardIssuedDescription: 'Kartica {{uid}} je spremna za upotrebu.',
    },
  },
};
